import { oneOf } from "decoders";

import {
  FundingSourceV2,
  QuestionEmployeeCount,
  QuestionFundingSource,
  SalesVolumeV2,
  EmployeeCountV3,
} from "@brexhq/next/types/brex-api";

import { LeadFormValues } from "../sharedTypes";

export const initialFormValues: LeadFormValues = {
  workEmail: "",
  firstName: "",
  lastName: "",
  mobileNumber: "",
  companyName: "",
  employeeCount: undefined,
  isEcomm: undefined,
  fundingSource: undefined,
  monthlySales: undefined,
  refCode: "",
  website: "",
  hasNoWebsite: false,
  password: "",
  industry: "",
  industryLabel: "",
  linkedinProfileUrl: "",
  fundingSources: [],
};

export const employeeCountOptionsOBS4 = [
  // TODO: AO-1803 - Edit the employee count options by EOY when we fully migrate to OBS4
  {
    label: "1 - 4",
    value: EmployeeCountV3.ONE_TO_FOUR,
  },
  {
    label: "5 - 20",
    value: EmployeeCountV3.FIVE_TO_TWENTY,
  },
  {
    label: "21 - 50",
    value: EmployeeCountV3.TWENTY_ONE_TO_FIFTY,
  },
  {
    label: "51 - 250",
    value: EmployeeCountV3.FIFTY_ONE_TO_TWO_HUNDRED_FIFTY,
  },
  {
    label: "251 - 1,000",
    value: EmployeeCountV3.TWO_HUNDRED_FIFTY_ONE_TO_ONE_THOUSAND,
  },
  {
    label: "1,000+",
    value: EmployeeCountV3.MORE_THAN_ONE_THOUSAND,
  },
];

export const employeeCountOptions = [
  {
    label: "1 - 4",
    value: QuestionEmployeeCount.ONE_TO_FOUR,
  },
  {
    label: "5 - 20",
    value: QuestionEmployeeCount.FIVE_TO_TWENTY,
  },
  {
    label: "21 - 50",
    value: QuestionEmployeeCount.TWENTY_ONE_TO_FIFTY,
  },
  {
    label: "51 - 100",
    value: QuestionEmployeeCount.FIFTY_ONE_TO_ONE_HUNDRED,
  },
  {
    label: "101 - 500",
    value: QuestionEmployeeCount.ONE_HUNDRED_ONE_TO_FIVE_HUNDRED,
  },
  {
    label: "501 - 2,500",
    value: QuestionEmployeeCount.FIVE_HUNDRED_ONE_TO_TWO_THOUSAND_FIVE_HUNDRED,
  },
  {
    label: "2,500+",
    value: QuestionEmployeeCount.MORE_THAN_TWO_THOUSAND_FIVE_HUNDRED,
  },
];

export const monthlySalesVolumeOptions = [
  {
    label: "Under $400K",
    value: SalesVolumeV2.UNDER_FOUR_HUNDRED_K,
  },
  {
    label: "$400K - $1M",
    value: SalesVolumeV2.FOUR_HUNDRED_K_TO_ONE_MM,
  },
  {
    label: "$1M - $12M",
    value: SalesVolumeV2.ONE_MM_TO_TWELVE_MM,
  },
  {
    label: "$12M+",
    value: SalesVolumeV2.MORE_THAN_TWELVE_MM,
  },
];

export const decodeEmployeeCountOptionValue = oneOf(
  employeeCountOptions.map((option) => option.value),
);

export type EmployeeCountOptionValue =
  typeof employeeCountOptions[number]["value"];

export const fundingSourceOptionsM4 = [
  {
    label: "Venture Capital / Angel Investor / Accelerator",
    value: QuestionFundingSource.PROFESSIONAL_INVESTMENT as string,
  },
  {
    label: "Crowdsourced",
    value: QuestionFundingSource.CROWDSOURCE as string,
  },
  {
    label: "Initial Coin Offering",
    value: QuestionFundingSource.ICO as string,
  },
  {
    label: "Family",
    value: QuestionFundingSource.FAMILY as string,
  },
  {
    label: "Self Funded",
    value: QuestionFundingSource.SELF_FUNDED as string,
  },
];

export const fundingSourceOptionsM5 = [
  {
    label: "Yes",
    value: QuestionFundingSource.PROFESSIONAL_INVESTMENT as string,
  },
  {
    label: "No",
    value: QuestionFundingSource.SELF_FUNDED as string,
  },
];

export const fundingSourceOptionsM7 = [
  {
    name: FundingSourceV2.ANGEL_INVESTMENT,
    label: "Angel Investment",
  },
  {
    name: FundingSourceV2.VENTURE_ACCELERATOR_GROWTH,
    label: "Venture Capital / Accelerator / Growth Equity",
  },
  {
    name: FundingSourceV2.PRIVATE_EQUITY,
    label: "Private Equity / Limited Partners",
  },
  { name: FundingSourceV2.LOAN_FUNDING, label: "Loan Funding" },
  { name: FundingSourceV2.BUSINESS_REVENUE, label: "Business Revenue" },
  {
    name: FundingSourceV2.PARENT_COMPANY,
    label: "Parent / Holding / Subsidiary Company",
  },
  { name: FundingSourceV2.SELF_FUNDED, label: "Self Funded" },
];

/**
 * Same as above but we need to have "value" keys instead of "name"
 */
export const fundingSourceMultiSelectOptionsM7 = [
  {
    value: FundingSourceV2.ANGEL_INVESTMENT,
    label: "Angel Investment",
  },
  {
    value: FundingSourceV2.VENTURE_ACCELERATOR_GROWTH,
    label: "Venture Capital / Accelerator / Growth Equity",
  },
  {
    value: FundingSourceV2.PRIVATE_EQUITY,
    label: "Private Equity / Limited Partners",
  },
  { value: FundingSourceV2.LOAN_FUNDING, label: "Loan Funding" },
  { value: FundingSourceV2.BUSINESS_REVENUE, label: "Business Revenue" },
  {
    value: FundingSourceV2.PARENT_COMPANY,
    label: "Parent / Holding / Subsidiary Company",
  },
  { value: FundingSourceV2.SELF_FUNDED, label: "Self Funded" },
];

export const decodeFundingSourceOptionValue = oneOf(
  fundingSourceOptionsM4.map((option) => option.value),
);

export type FundingSourceOptionValue = ReturnType<
  typeof decodeFundingSourceOptionValue["verify"]
>;
